import {
  MODULES_COEUR,
  estModulePilotable,
  estModuleService,
  modulesAVenir,
  sujet,
  type Action,
  type Module,
  type ModuleOptionnel,
  type ModulePilotable,
  type Sujet,
} from '@artosera/shared';
import type {
  CompteSession,
  GalerieSession,
  GroupeSession,
  SessionUtilisateur,
} from '../api/types.js';

/**
 * Sujet `@artosera/shared` d'une galerie de session : son rôle et ses droits
 * fins réels (5.1), tels que renvoyés par `GET /v1/auth/me`.
 */
export function sujetDeLaGalerie(galerie: GalerieSession): Sujet {
  return sujet(galerie.role, galerie.droits);
}

/**
 * Une galerie ouvre-t-elle cette action ? La liste `actions` de l'API fait
 * foi (5.4, calculée par la même matrice que `can()` côté serveur) : c'est
 * elle que la régie consulte pour afficher ou masquer un lien ou un bouton.
 *
 * `can(sujetDeLaGalerie(galerie), action)` reste la fonction de référence de
 * `@artosera/shared` — elle sert de repli là où l'API ne renvoie aucune
 * liste `actions` (la portée groupe, `sujetGroupeDuCompte`) et de garde de
 * cohérence : `sujet.test.ts` prouve que les deux concordent sur les
 * fixtures.
 */
export function galerieALaction(galerie: GalerieSession, action: Action): boolean {
  return galerie.actions.includes(action);
}

/**
 * Les modules EFFECTIFS d'une galerie (D21), tels que `GET /v1/auth/me` les
 * rend : l'API les calcule par la fonction unique de `@artosera/shared`
 * (`modulesDeLaGalerie`), coupures globales comprises — ce que la régie ne
 * pourrait pas savoir seule. Liste absente : le cœur seul, plutôt qu'une
 * entrée qui échouerait à l'usage.
 */
export function modulesDeLaSession(galerie: GalerieSession): readonly Module[] {
  return galerie.modules ?? MODULES_COEUR;
}

/** La galerie dispose-t-elle de ce module ? Masque navigation, ⌘K et boutons. */
export function galerieAModule(galerie: GalerieSession, module: Module): boolean {
  return modulesDeLaSession(galerie).includes(module);
}

/**
 * Les modules CONSULTABLES en lecture seule (`modulesLectureSeule` de
 * `GET /v1/auth/me`) : visibles et lisibles, jamais modifiables. Un champ
 * absent ou malformé n'en donne aucun. Un service (site, boutique) n'en est
 * jamais, même si l'API le rendait : sans achat, il reste invisible.
 */
export function modulesLectureSeuleDeLaSession(
  galerie: GalerieSession,
): readonly ModulePilotable[] {
  const brut: unknown = galerie.modulesLectureSeule;
  if (!Array.isArray(brut)) return [];
  return brut.filter(
    (module): module is ModulePilotable =>
      typeof module === 'string' && estModulePilotable(module) && !estModuleService(module),
  );
}

/** Ce module est-il consultable mais en lecture seule pour cette galerie ? */
export function moduleEnLectureSeule(galerie: GalerieSession, module: Module): boolean {
  return (modulesLectureSeuleDeLaSession(galerie) as readonly Module[]).includes(module);
}

/**
 * La galerie peut-elle ÉCRIRE dans ce module ? Effectif ET pas en lecture
 * seule : la question des boutons de création, y compris sur une galerie « de
 * page » (`auth/lectureSeule.ts`) où les modules en lecture seule sont visibles.
 */
export function galerieEcritModule(galerie: GalerieSession, module: Module): boolean {
  return galerieAModule(galerie, module) && !moduleEnLectureSeule(galerie, module);
}

/**
 * La galerie voit-elle ce module, en écriture OU en lecture seule ? Ce que
 * la navigation et les pages de consultation demandent ; `galerieAModule`
 * reste la question « puis-je y écrire ? » (modules effectifs seuls).
 */
export function galerieVoitModule(galerie: GalerieSession, module: Module): boolean {
  return galerieAModule(galerie, module) || moduleEnLectureSeule(galerie, module);
}

/**
 * Les modules À VENIR de cette galerie : ceux des paliers de déblocage que
 * `GET /v1/auth/me` dit fermés par le verrou de palier (`modulesVerrouilles`), et
 * que la galerie n'a pas d'effectif. Un module d'un palier débloqué mais non
 * souscrit n'en fait jamais partie : il reste invisible. Fonction pure de la session, donc
 * sans réseau : l'aperçu d'un module à venir ne peut appeler aucune de ses routes.
 */
export function modulesAVenirDeLaSession(
  session: Pick<SessionUtilisateur, 'modulesVerrouilles'>,
  galerie: GalerieSession,
): readonly ModuleOptionnel[] {
  // Un module consultable en lecture seule n'est pas « à venir » : il est là.
  return modulesAVenir(session.modulesVerrouilles ?? [], [
    ...modulesDeLaSession(galerie),
    ...modulesLectureSeuleDeLaSession(galerie),
  ]);
}

/**
 * Le rattachement de groupe du compte qui couvre cette galerie, s'il y en a
 * un. Un compte peut appartenir à plusieurs groupes ; seul celui qui liste la
 * galerie courante gouverne l'entrée « Groupe » et le sujet de groupe pour
 * cette galerie (5.2).
 */
export function groupeDeLaGalerie(
  compte: CompteSession,
  galerie: GalerieSession,
): GroupeSession | null {
  const groupes = compte.groupes ?? [];
  return groupes.find((groupe) => groupe.galeries.includes(galerie.id)) ?? null;
}

/** Portée d'autorisation de groupe, commune à la purge et aux clés de console. */
export function empreinteDroitsGroupe(groupe: GroupeSession | null): string | null {
  return groupe
    ? JSON.stringify([groupe.id, groupe.slug, groupe.role, [...groupe.galeries].sort()])
    : null;
}

/**
 * Sujet de groupe (5.2), pour l'entrée « Groupe » de la navigation (3.2) :
 * `null` quand le compte n'a pas de rattachement de groupe pour cette
 * galerie. Écart assumé : `GET /v1/auth/me` n'expose pas toujours
 * `compte.groupes` (voir `api/types.ts`) ; cette fonction rend `null` tant
 * que le champ est absent, plutôt que de deviner un rôle de groupe qui
 * n'existe pas dans la réponse.
 */
export function sujetGroupeDuCompte(compte: CompteSession, galerie: GalerieSession): Sujet | null {
  const groupe = groupeDeLaGalerie(compte, galerie);
  if (!groupe) return null;
  return sujet(groupe.role);
}
